import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Api } from 'bimplus-websdk';
import { BIMPLUS_API } from '@web-portal/domains/shared/bimplus-core';
import { CreateModelParams, Model } from '@web-portal/domains/shared/domain-models';
import './bimplus-websdk.augmentation';

/**
 * ModelsAdapterService - Adapter over bimplus-websdk Models (Divisions) API.
 */
@Injectable({
  providedIn: 'root',
})
export class ModelsAdapterService {
  private readonly api: Api = inject(BIMPLUS_API);

  /**
   * Retrieves models (divisions/disciplines) for a specific project.
   *
   * @param projectId - The ID of the project to get models for.
   * @returns Observable of Model array.
   */
  getModels(projectId: string): Observable<Model[]> {
    return this.api.rxRequest(
      (
        this.api.projects as unknown as { getModels(projectId: string): Promise<Model[]> }
      ).getModels(projectId)
    );
  }

  /**
   * Creates a new model (division) within a project.
   *
   * @param params - Parameters for the new model.
   * @returns Observable of created Model.
   */
  createModel(params: CreateModelParams): Observable<Model> {
    return this.api.rxRequest(
      (
        this.api.projects as unknown as {
          postModel(projectId: string, payload: string): Promise<Model>;
        }
      ).postModel(params.projectId, JSON.stringify({ name: params.name }))
    );
  }

  /**
   * Deletes a model (division) by ID.
   *
   * @param divisionId - The ID of the division/model to delete.
   * @returns Observable that completes when deletion is successful.
   */
  deleteModel(divisionId: string) {
    return this.api.rxRequest(
      this.api.request.del(this.api.getApiTeamUrl() + 'divisions/' + divisionId)
    );
  }

  /**
   * Renames a model (division) by ID.
   *
   * @param divisionId - The ID of the division/model to rename.
   * @param name - The new name for the model.
   * @returns Observable that completes when renaming is successful.
   */
  renameModel(divisionId: string, name: string) {
    return this.api.rxRequest(
      this.api.request.put(
        this.api.getApiTeamUrl() + 'divisions/' + divisionId,
        JSON.stringify({ name }),
        undefined
      )
    );
  }

  /**
   * Gets the thumbnail URL for a model (object/division).
   *
   * The URL pattern is: {apiTeamUrl}objects/{modelId}/thumbnail/download
   *
   * @param modelId - The ID of the model/object.
   * @returns The full URL to download the thumbnail image.
   */
  getThumbnailUrl(modelId: string): string {
    return `${this.api.getApiTeamUrl()}objects/${modelId}/thumbnail/download`;
  }

  /**
   * Builds an authenticated download URL for a model (division).
   *
   * URL pattern: {apiTeamUrl}divisions/{divisionId}/download?revision={revision}&api-token={token}
   *
   * @param divisionId - The ID of the division/model to download.
   * @param revision - The revision number to download.
   * @returns The full authenticated URL to download the model file.
   */
  getDownloadUrl(divisionId: string, revision: number): string {
    const url = new URL(`${this.api.getApiTeamUrl()}divisions/${divisionId}/download`);
    url.searchParams.set('revision', String(revision));
    const token = this.api.getAccessToken();
    if (token) {
      url.searchParams.set('api-token', token);
    }
    return url.toString();
  }
}
